import React, { useState, useRef, useEffect } from 'react';
import './UserProfile.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { getUserProfile, updatePassword, updateUserProfile } from '../../dbApiFunctions';
import { PROFILE_USER_AVATAR } from '../../constant';
import { showSuccessAlert, showErrorAlert, showWarningAlert, showInfoAlert, showConfirmDialog } from '../../utils/sweetAlertConfig';
import '../../styles/sweetAlert.css';

const UserProfile = () => {
    const [isEditable, setIsEditable] = useState(false);
    const [showPersonalInfo, setShowPersonalInfo] = useState(true);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [profileImage, setProfileImage] = useState(PROFILE_USER_AVATAR);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        const email = sessionStorage.getItem("email");
        const fetchUserData = async () => {
            const userProfile = await getUserProfile(email);
            if (userProfile.status) {
                setUserData(userProfile.user);
                // console.log("User data set successfully:", userProfile.user);
            } else {
                showErrorAlert(userProfile.message, 'Error', 'Please try again');
            }
        };

        if (email) {
            fetchUserData();
        } else {
            console.error("No email found in session storage");
        }
    }, []);

    // Add a second useEffect to update form data when userData changes
    useEffect(() => {
        if (userData) {
            // console.log("Updating form data with userData:", userData);
            setFormData({
                firstName: userData.userName || "",
                lastName: userData.lastName || "",
                email: userData.email || "",
                phone: userData.phone || "",
                oldPassword: "",
                newPassword: "",
                confirmPassword: ""
            });
        }
    }, [userData]);


    // Form state
    const [formData, setFormData] = useState({
        firstName: userData?.userName || "",
        lastName: userData?.lastName || "",
        email: userData?.email || "",
        phone: userData?.phone || "",
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    const toggleEditing = () => {
        setIsEditable(!isEditable);
    };

    const handleImageClick = () => {
        // if (isEditable) {
        //     fileInputRef.current.click();
        // }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setIsUploading(true);

            // Create a preview URL for the selected image
            const imageUrl = URL.createObjectURL(file);

            // Simulate upload delay (replace with actual API call)
            setTimeout(() => {
                setProfileImage(imageUrl);
                setIsUploading(false);
                // Here you would typically upload the file to your server
                // and update the profile with the new image URL
            }, 1000);
        }
    };

    const handleInputChange = (e, field) => {
        setFormData({
            ...formData,
            [field]: e.target.value
        });
    };

    const handleLogout = () => {
        // Clear session storage
        sessionStorage.removeItem("username");
        sessionStorage.removeItem("password");

        // Redirect to login page or home page
        window.location.href = "/webapp";
    };

    const handleSaveChanges = async () => {
        const email = sessionStorage.getItem("email");
        const userData = {
            userName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone
        };

        const result = await updateUserProfile(email, userData);

        if (result.status) {
            showSuccessAlert(
                'Profile Updated!',
                `
                <div style="text-align: left; margin-top: 1rem;">
                    <div style="margin-bottom: 0.5rem;">
                        <i class="fas fa-user" style="color: #4CAF50; margin-right: 0.5rem;"></i>
                        <strong>First Name:</strong> ${formData.firstName}
                    </div>
                    <div style="margin-bottom: 0.5rem;">
                        <i class="fas fa-user" style="color: #4CAF50; margin-right: 0.5rem;"></i>
                        <strong>Last Name:</strong> ${formData.lastName}
                    </div>
                    <div style="margin-bottom: 0.5rem;">
                        <i class="fas fa-envelope" style="color: #4CAF50; margin-right: 0.5rem;"></i>
                        <strong>Email:</strong> ${formData.email}
                    </div>
                    <div style="margin-bottom: 0.5rem;">
                        <i class="fas fa-phone" style="color: #4CAF50; margin-right: 0.5rem;"></i>
                        <strong>Phone:</strong> ${formData.phone}
                    </div>
                </div>
                `,
                'Your profile has been updated successfully'
            );
            setIsEditable(false);
        } else {
            showErrorAlert(result.message, 'Update Failed', 'Please try again');
        }
    };

    const handleResetPassword = async () => {
        if (formData.newPassword !== formData.confirmPassword) {
            showErrorAlert('New password and confirm password do not match!', 'Password Mismatch', 'Please make sure both passwords are identical');
            return;
        }

        const email = sessionStorage.getItem("email");
        const password = formData.newPassword;
        const updatePassword = await updatePassword(email, password);

        if (updatePassword.status) {
            showSuccessAlert(
                'Password Updated!',
                'Your password has been changed successfully.',
                'Please use your new password for future logins'
            );
            setIsEditable(false);
        } else {
            showErrorAlert(updatePassword.message, 'Update Failed', 'Please try again');
        }
    };

    // const message = `
    //         Password reset successful!

    //         New password: ${formData.newPassword}
    //     `;
    //     showErrorAlert(message);
    //     setIsEditable(false);

    return (
        <div className="page-container mt-16">
            <div className="account-container">
                <h1 className="title">My Account</h1>

                <div className="account-actions">
                    <button
                        className={`edit-btn ${isEditable ? 'green' : 'purple'}`}
                        onClick={toggleEditing}
                    >
                        <i className="fas fa-edit"></i> {isEditable ? 'Save' : 'Edit'}
                    </button>

                    <button
                        className="logout-btn"
                        onClick={handleLogout}
                    >
                        <i className="fas fa-sign-out-alt"></i> Logout
                    </button>
                </div>

                <div className="profile-header">
                    <div className="image-container">
                        <img
                            alt="Profile"
                            src={profileImage}
                            onClick={handleImageClick}
                            className={isEditable ? "editable" : ""}
                        />
                        {isEditable && (
                            <button
                                className="img-edit-btn"
                                onClick={handleImageClick}
                                disabled={isUploading}
                            >
                                {isUploading ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                    <i className="fas fa-camera"></i>
                                )}
                            </button>
                        )}
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept="image/*"
                            className="file-input"
                        />
                    </div>
                    <div className="profile-summary">
                        <h2>{formData.firstName} {formData.lastName}</h2>
                        <p>{formData.email}</p>
                        <p>{formData.phone}</p>
                    </div>
                </div>

                <div className="section">
                    <div
                        className="section-header"
                        onClick={() => setShowPersonalInfo(!showPersonalInfo)}
                    >
                        <h2>Personal Information</h2>
                        <i className={`fas fa-chevron-${showPersonalInfo ? 'up' : 'down'}`}></i>
                    </div>
                    {showPersonalInfo && (
                        <div className="section-content">
                            <div className="form-group">
                                <label>First Name</label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    disabled={!isEditable}
                                    onChange={(e) => handleInputChange(e, 'firstName')}
                                />
                            </div>
                            <div className="form-group">
                                <label>Last Name</label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    disabled={!isEditable}
                                    onChange={(e) => handleInputChange(e, 'lastName')}
                                />
                            </div>
                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    disabled={!isEditable}
                                    onChange={(e) => handleInputChange(e, 'email')}
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="text"
                                    value={formData.phone}
                                    disabled={!isEditable}
                                    onChange={(e) => handleInputChange(e, 'phone')}
                                />
                            </div>
                            <div className="form-group full-width">
                                <button
                                    className="action-btn"
                                    onClick={handleSaveChanges}
                                    disabled={!isEditable}
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* <div className="section">
                    <div
                        className="section-header"
                        onClick={() => setShowChangePassword(!showChangePassword)}
                    >
                        <h2>Change Password</h2>
                        <i className={`fas fa-chevron-${showChangePassword ? 'up' : 'down'}`}></i>
                    </div>
                    {showChangePassword && (
                        <div className="section-content">
                            <div className="form-group">
                                <label>Old Password</label>
                                <input
                                    type="password"
                                    value={formData.oldPassword}
                                    disabled={!isEditable}
                                    onChange={(e) => handleInputChange(e, 'oldPassword')}
                                />
                            </div>
                            <div className="form-group">
                                <label>New Password</label>
                                <input
                                    type="password"
                                    value={formData.newPassword}
                                    disabled={!isEditable}
                                    onChange={(e) => handleInputChange(e, 'newPassword')}
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirm New Password</label>
                                <input
                                    type="password"
                                    value={formData.confirmPassword}
                                    disabled={!isEditable}
                                    onChange={(e) => handleInputChange(e, 'confirmPassword')}
                                />
                            </div>
                            <div className="form-group full-width">
                                <button
                                    className="action-btn"
                                    onClick={handleResetPassword}
                                    disabled={!isEditable}
                                >
                                    Reset Password
                                </button>
                            </div>
                        </div>
                    )}
                </div> */}
            </div>
        </div>
    );
};

export default UserProfile;